import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { type FilterWithValue, FilterDefinitionTypeEnum } from '../filters'
import { readToggle } from './useFilterValue'

/**
 * Composable for formatting filter labels for display
 */
export function useFilterLabels() {
  const { t } = useI18n()

  /**
   * Gets the display label for a filter's selected value
   */
  function getSelectedFilterLabels(filterWithValue: FilterWithValue): string {
    if (filterWithValue.filter.type === FilterDefinitionTypeEnum.BOOLEAN) {
      return filterWithValue.value ? t('Yes') : t('No')
    }

    if ([FilterDefinitionTypeEnum.SELECT, FilterDefinitionTypeEnum.ASYNC_SELECT].includes(filterWithValue.filter.type)) {
      const labels = filterWithValue.filter.options
        .filter(option => (filterWithValue.value as string[]).includes(String(option.value)))
        .map(option => option.label)

      let label: string
      if (labels.length <= 3) {
        label = labels.join(', ')
      } else {
        const firstLabel = labels[0]
        const remaining = labels.length - 1
        label = t('{firstLabel} and {count} more', { firstLabel, count: remaining })
      }

      return withToggleLabel(filterWithValue, label)
    }

    if (filterWithValue.filter.type === FilterDefinitionTypeEnum.PERIOD) {
      // Bounds carrying a time are ISO 8601 and contain colons of their own, so the separator is
      // the colon introducing the next bound — a bound always opens with a four-digit year.
      const parts = (filterWithValue.value as string).split(/^:|:$|:(?=\d{4}-)/)
      const format = (bound: string) => (
        bound && filterWithValue.filter.with_time ? dayjs(bound).format('L LT') : bound
      )
      const from = format(parts[0] || '')
      const to = format(parts[1] || '')

      if (from && to) {
        return t('{fromDate} → {toDate}', { fromDate: from, toDate: to })
      } else if (from) {
        return t('From date: {date}', { date: from })
      } else if (to) {
        return t('To date: {date}', { date: to })
      }
      return ''
    }

    if (filterWithValue.filter.type === FilterDefinitionTypeEnum.TEXT) {
      return String(filterWithValue.value)
    }

    throw Error('Filter type not supported')
  }

  /**
   * States the toggle only when it departs from its default: spelling out the
   * usual state would put the same words on every chip.
   */
  function withToggleLabel(filterWithValue: FilterWithValue, label: string): string {
    const toggle = filterWithValue.filter.toggle
    if (!toggle) {
      return label
    }

    const isOn = readToggle(filterWithValue.value as string[], toggle)
    if (isOn === toggle.default) {
      return label
    }

    return t('{labels} · {toggle}: {state}', {
      labels: label,
      toggle: toggle.label,
      state: isOn ? t('Yes') : t('No'),
    })
  }

  return {
    getSelectedFilterLabels
  }
}

